import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DbProfile, getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { authenticateAdmin, authenticateClient } from '../services/shipmentStore';
import { AuthUser, UserRole } from '../types/shipment';

const AUTH_KEY = 'alshiraa_auth_v1';

interface LoginResult {
  ok: boolean;
  error?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isCloudMode: boolean;
  loginClient: (email: string, password: string) => Promise<LoginResult>;
  loginAdmin: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<LoginResult>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const mapProfile = (profile: DbProfile): AuthUser => ({
  email: profile.email,
  name: profile.name,
  role: profile.role,
  company: profile.company ?? undefined,
  clientCode: profile.client_code ?? undefined,
});

const AuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(isSupabaseConfigured());
  const isCloudMode = isSupabaseConfigured();

  useEffect(() => {
    if (!isCloudMode) {
      try {
        const raw = localStorage.getItem(AUTH_KEY);
        if (raw) setUser(JSON.parse(raw) as AuthUser);
      } catch {
        localStorage.removeItem(AUTH_KEY);
      }
      setLoading(false);
      return undefined;
    }

    const supabase = getSupabase()!;

    const loadSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setUser(null);
        setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profile) {
        setUser(mapProfile(profile as DbProfile));
      } else {
        setUser({
          email: session.user.email || '',
          name: session.user.email?.split('@')[0] || 'User',
          role: 'client',
        });
      }
      setLoading(false);
    };

    loadSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session?.user) {
        setUser(null);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profile) {
        setUser(mapProfile(profile as DbProfile));
      }
    });

    return () => subscription.unsubscribe();
  }, [isCloudMode]);

  const persistLocal = (next: AuthUser | null) => {
    setUser(next);
    if (next) localStorage.setItem(AUTH_KEY, JSON.stringify(next));
    else localStorage.removeItem(AUTH_KEY);
  };

  const loginClient = async (email: string, password: string): Promise<LoginResult> => {
    if (!isCloudMode) {
      const client = authenticateClient(email, password);
      if (!client) return { ok: false, error: 'invalid' };
      persistLocal({ email: client.email, name: client.name, role: 'client', company: client.company });
      return { ok: true };
    }

    const supabase = getSupabase()!;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.user) return { ok: false, error: 'invalid' };

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .single();

    if (profileError || !profile) {
      await supabase.auth.signOut();
      return { ok: false, error: 'invalid' };
    }

    if ((profile as DbProfile).role !== 'client') {
      await supabase.auth.signOut();
      return { ok: false, error: 'not_client' };
    }

    setUser(mapProfile(profile as DbProfile));
    return { ok: true };
  };

  const loginAdmin = async (email: string, password: string): Promise<LoginResult> => {
    if (!isCloudMode) {
      const admin = authenticateAdmin(email, password);
      if (!admin) return { ok: false, error: 'invalid' };
      persistLocal({ email: admin.email, name: admin.name, role: 'admin' });
      return { ok: true };
    }

    const supabase = getSupabase()!;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.user) return { ok: false, error: 'invalid' };

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .single();

    if (profileError || !profile) {
      await supabase.auth.signOut();
      return { ok: false, error: 'invalid' };
    }

    if ((profile as DbProfile).role !== 'admin') {
      await supabase.auth.signOut();
      return { ok: false, error: 'not_admin' };
    }

    setUser(mapProfile(profile as DbProfile));
    return { ok: true };
  };

  const logout = async () => {
    if (isCloudMode) {
      await getSupabase()?.auth.signOut();
    }
    persistLocal(null);
    setUser(null);
  };

  const resetPassword = async (email: string): Promise<LoginResult> => {
    if (!isCloudMode) {
      return { ok: false, error: 'invalid' };
    }
    const supabase = getSupabase()!;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/portal`,
    });
    if (error) return { ok: false, error: 'invalid' };
    return { ok: true };
  };

  const value = useMemo(
    () => ({ user, loading, isCloudMode, loginClient, loginAdmin, logout, resetPassword }),
    [user, loading, isCloudMode]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthProviderInner>{children}</AuthProviderInner>
);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const useRequireRole = (role: UserRole) => {
  const { user } = useAuth();
  return user?.role === role;
};
